import { CardDetails, EncryptedCardResponse } from "../types/add-to-wallet";

export async function generateKeyPair(): Promise<CryptoKeyPair> {
	return crypto.subtle.generateKey(
		{
			name: "RSA-OAEP",
			modulusLength: 4096,
			publicExponent: new Uint8Array([1, 0, 1]),
			hash: "SHA-256",
		},
		true,
		["encrypt", "decrypt"]
	);
}

export async function exportPublicKey(key: CryptoKey): Promise<string> {
	const spki = await crypto.subtle.exportKey("spki", key);
	return bufferToBase64(new Uint8Array(spki));
}

export async function decryptCardPayload(
	{ cardData, aesKey, iv, tag }: EncryptedCardResponse,
	privateKey: CryptoKey
): Promise<CardDetails> {
	const aesKeyBytes = await crypto.subtle.decrypt(
		{ name: "RSA-OAEP" },
		privateKey,
		base64ToBuffer(aesKey)
	);

	const aesCryptoKey = await crypto.subtle.importKey(
		"raw",
		aesKeyBytes,
		{ name: "AES-GCM" },
		false,
		["decrypt"]
	);

	const ivBuf = base64ToBuffer(iv);
	const cipherBytes = new Uint8Array(base64ToBuffer(cardData));
	const tagBytes = new Uint8Array(base64ToBuffer(tag));
	const combined = new Uint8Array(cipherBytes.length + tagBytes.length);
	combined.set(cipherBytes);
	combined.set(tagBytes, cipherBytes.length);

	const plainBuffer = await crypto.subtle.decrypt(
		{
			name: "AES-GCM",
			iv: ivBuf,
		},
		aesCryptoKey,
		combined
	);

	const json = new TextDecoder().decode(plainBuffer);
	return JSON.parse(json) as CardDetails;
}

export function base64ToBuffer(base64: string): ArrayBuffer {
	const clean = base64.replace(/\s+/g, "");
	const bin = atob(clean);
	const out = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
	return out.buffer;
}

function bufferToBase64(bytes: Uint8Array): string {
	let binary = "";
	for (const b of bytes) binary += String.fromCharCode(b);
	return btoa(binary);
}
